/**
 * ZEUS-P0-2: when the in-app assistant hands a message to an agent, it saves a placeholder
 * ("<Agent> is working on this...", agentDelivery.placeholder) and the agent task writes the real
 * answer into the conversation minutes later. Messages are read once (getDocs), so poll while ANY
 * placeholder is still open (pending or running), even if the user has sent more messages since, and
 * stop when it closes or after a cap. Every server path closes the placeholder; only a task killed
 * mid-run (out of memory, or the 540s task kill) cannot, so a placeholder still open past the cap is shown as not
 * finished (closeStalePlaceholders, applied to each fetch) and polling stops (RVW-B M1).
 */
import type { ConversationMessage } from '../../types/ai-sales-chat';

export const AGENT_DELIVERY_REFETCH_MS = 4000;
/**
 * How long the client waits for an answer before showing "didn't finish" (RVW-B delta 2). It must be
 * later than the latest an agent can still finish: the task may claim a hand-off up to MAX_CLAIM_AGE_MS
 * (8 min) after it was created and then run up to AGENT_RUN_DEADLINE_MS (8 min), both in
 * functions-ai/src/chat-v2/inAppAgentDelivery.ts. 18 min = 16 + 2 min margin for clock skew. Otherwise
 * a user told "didn't finish" could resend while the first run still completes, and the agent acts twice.
 * Pinned by scripts/guards/check-agent-handoff-timing.mjs.
 */
export const AGENT_DELIVERY_MAX_WAIT_MS = 18 * 60 * 1000;

/**
 * Is this message an agent placeholder still waiting for its answer, within the polling cap?
 * @param message a conversation message
 * @param nowMs the current time
 * @returns true while it is open and recent
 */
function isOpenPlaceholder(message: ConversationMessage, nowMs: number): boolean {
  const delivery = message.metadata?.agentDelivery;
  if (message.role !== 'assistant' || delivery?.placeholder !== true) return false;
  if (delivery.status !== 'pending' && delivery.status !== 'running') return false;
  const createdMs = typeof message.createdAt?.toMillis === 'function' ? message.createdAt.toMillis() : NaN;
  return Number.isFinite(createdMs) && nowMs - createdMs <= AGENT_DELIVERY_MAX_WAIT_MS;
}

/**
 * Refetch interval for a conversation's messages.
 * @param messages the messages, oldest first
 * @param nowMs the current time (injected for tests)
 * @returns the interval in ms while an agent answer is pending, else false
 */
export function agentDeliveryRefetchInterval(
  messages: ConversationMessage[] | undefined,
  nowMs: number = Date.now(),
): number | false {
  return messages?.some((m) => isOpenPlaceholder(m, nowMs)) ? AGENT_DELIVERY_REFETCH_MS : false;
}

/** What a placeholder still open past the cap says instead of "the answer will appear here". */
export const AGENT_DELIVERY_STALE_TEXT = "This request didn't finish. Please try again.";

/**
 * Show placeholders still open past the polling cap as failed, so nothing promises an answer that is
 * not coming. Returns new message objects; the input is not changed.
 * @param messages the messages as fetched
 * @param nowMs the current time (injected for tests)
 * @returns the messages, with stale placeholders closed
 */
export function closeStalePlaceholders(messages: ConversationMessage[], nowMs: number = Date.now()): ConversationMessage[] {
  return messages.map((m) => {
    const delivery = m.metadata?.agentDelivery;
    if (m.role !== 'assistant' || delivery?.placeholder !== true) return m;
    if (delivery.status !== 'pending' && delivery.status !== 'running') return m;
    const createdMs = typeof m.createdAt?.toMillis === 'function' ? m.createdAt.toMillis() : NaN;
    if (!Number.isFinite(createdMs) || nowMs - createdMs <= AGENT_DELIVERY_MAX_WAIT_MS) return m;
    return {
      ...m,
      content: AGENT_DELIVERY_STALE_TEXT,
      metadata: { ...m.metadata, agentDelivery: { ...delivery, status: 'failed' } },
    };
  });
}
